import type { ReactNode } from "react";
import { cn } from "@nexus/ui";
import {
  ADMIN_LABEL_CLASS,
  ADMIN_PANEL_CLASS,
  ADMIN_PANEL_HEADER_CLASS,
  ADMIN_TABLE_ROW_HEIGHT_PX,
} from "./admin-layout-tokens";

interface AdminDataPanelProps {
  /** Names the rows. Omit only when the region above already named them. */
  title?: string;
  /** How many rows are behind the panel, after its own controls have run. */
  count?: ReactNode;
  /**
   * Search and toggles scoped to these rows alone. They live in the panel's
   * header, never in the page filter bar: a control that narrows one table is
   * not a control that narrows the page.
   */
  controls?: ReactNode;
  /**
   * How many rows fit before the panel scrolls in place. Row counts here are
   * unbounded — one per tenant, one per model — so without a cap the tab's
   * length is set by whatever the window happened to contain and the panels
   * below drift out of reach on a busy day. Omit for a panel whose row count
   * is bounded by something other than the data.
   */
  maxRows?: number;
  children: ReactNode;
}

/**
 * A titled card holding one table.
 *
 * The header band is the only place a table-scoped control may mount, and the
 * cap is expressed in rows rather than pixels so that a panel always ends on a
 * row boundary — a half-row at the fold reads as a rendering fault, not as an
 * invitation to scroll.
 */
export function AdminDataPanel({ title, count, controls, maxRows, children }: AdminDataPanelProps) {
  const hasHeader = title != null || count != null || controls != null;
  // One row over the cap for the sticky header, which shares the scroll box.
  const maxHeight = maxRows == null ? undefined : (maxRows + 1) * ADMIN_TABLE_ROW_HEIGHT_PX;

  return (
    <section className={ADMIN_PANEL_CLASS}>
      {hasHeader ? (
        <div className={ADMIN_PANEL_HEADER_CLASS}>
          {title ? <span className={cn(ADMIN_LABEL_CLASS, "shrink-0")}>{title}</span> : null}
          {count ? (
            <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
          ) : null}
          {controls ? (
            <div className="ml-auto flex flex-wrap items-center gap-2">{controls}</div>
          ) : null}
        </div>
      ) : null}

      <div className="overflow-y-auto" style={{ maxHeight }}>
        {children}
      </div>
    </section>
  );
}
